import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';

interface SapData {
  id: string;
  name: string;
  type: string;
  status: string;
  lastModified: string;
  size: string;
}

const Viewer: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SapData[]>([]);
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [error, setError] = useState<string | null>(null);

  // Simulated data - replace with actual API call
  const mockData: SapData[] = [
    {
      id: '1',
      name: 'SAP_HR_Employee_Data',
      type: 'Table',
      status: 'Active',
      lastModified: '2024-01-15 10:30:00',
      size: '2.5 MB'
    },
    {
      id: '2',
      name: 'SAP_FI_GL_Accounts',
      type: 'View',
      status: 'Active',
      lastModified: '2024-01-14 15:45:00',
      size: '1.8 MB'
    },
    {
      id: '3',
      name: 'SAP_MM_Material_Master',
      type: 'Table',
      status: 'Inactive',
      lastModified: '2024-01-13 09:20:00',
      size: '5.2 MB'
    },
    {
      id: '4',
      name: 'SAP_SD_Customer_Orders',
      type: 'View',
      status: 'Active',
      lastModified: '2024-01-12 14:15:00',
      size: '3.1 MB'
    },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setData(mockData);
    } catch (err) {
      setError('Error loading data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredData = data.filter(item => {
    const matchesFilter = item.name.toLowerCase().includes(filter.toLowerCase());
    const matchesType = typeFilter === 'all' || item.type === typeFilter;
    return matchesFilter && matchesType;
  });

  const getStatusColor = (status: string) => {
    return status === 'Active' ? 'success' : 'error';
  };

  const getTypeColor = (type: string) => {
    return type === 'Table' ? 'primary' : 'secondary';
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          SAP Data Viewer
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Visualize and explore SAP system data
        </Typography>
      </Box>

      {/* Filters and Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Search by name"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                label="Type"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="Table">Table</MenuItem>
                <MenuItem value="View">View</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={5}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchData}
                disabled={loading}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                disabled={loading || filteredData.length === 0}
              >
                Export
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Content */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <Grid container spacing={2}>
            {filteredData.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3,
                    }
                  }}
                >
                  <CardHeader
                    avatar={<VisibilityIcon color="primary" />}
                    title={item.name}
                    subheader={`Last modified: ${item.lastModified}`}
                    titleTypographyProps={{ fontSize: '1rem', fontWeight: 'bold' }}
                    subheaderTypographyProps={{ fontSize: '0.8rem' }}
                  />
                  <CardContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          Type:
                        </Typography>
                        <Chip
                          label={item.type}
                          size="small"
                          color={getTypeColor(item.type) as any}
                          variant="outlined"
                        />
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          Status:
                        </Typography>
                        <Chip
                          label={item.status}
                          size="small"
                          color={getStatusColor(item.status) as any}
                        />
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          Size:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {item.size}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                      <Button size="small" variant="outlined" fullWidth>
                        View Details
                      </Button>
                      <Button size="small" variant="outlined" fullWidth>
                        Download
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {!loading && !error && filteredData.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No results found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting the search filters
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Viewer; 